import { create } from "zustand";
import { type FacetSelection } from "@/content/facets";
import { getConceptAxes } from "@/store/customContentStore";

const STORAGE_KEY = "hippo.facets";

/**
 * Axis-agnostic selection state. We store only the user's *deviations* — which
 * axes are turned off and which values are turned off — so the store reconciles
 * automatically as Game-Concept axes/values are added or removed in the studio.
 * New axes default ON with all values active.
 */
interface Persisted {
  disabledAxes: string[];
  disabledValues: Record<string, string[]>;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return { disabledAxes: p.disabledAxes ?? [], disabledValues: p.disabledValues ?? {} };
    }
  } catch {
    /* ignore */
  }
  return { disabledAxes: [], disabledValues: {} };
}

interface FacetState extends Persisted {
  isAxisEnabled: (key: string) => boolean;
  isValueOn: (key: string, value: string) => boolean;
  activeCount: (key: string) => number;
  toggleAxis: (key: string) => void;
  toggleValue: (key: string, value: string) => void;
  setAxisValues: (key: string, on: boolean) => void;
  enableAllAxes: () => void;
  disableAllAxes: () => void;
  resetAll: () => void;
  activeSelection: () => FacetSelection;
  activeAxisCount: () => number;
}

export const useFacetStore = create<FacetState>((set, get) => {
  const persist = () => {
    const { disabledAxes, disabledValues } = get();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ disabledAxes, disabledValues }));
  };
  const valuesOf = (key: string) => getConceptAxes().find((a) => a.key === key)?.values ?? [];

  return {
    ...load(),

    isAxisEnabled: (key) => !get().disabledAxes.includes(key),
    isValueOn: (key, value) => !get().disabledAxes.includes(key) && !(get().disabledValues[key] ?? []).includes(value),
    activeCount: (key) =>
      get().disabledAxes.includes(key) ? 0 : valuesOf(key).filter((v) => !(get().disabledValues[key] ?? []).includes(v)).length,

    toggleAxis: (key) => {
      const d = get().disabledAxes;
      set({ disabledAxes: d.includes(key) ? d.filter((x) => x !== key) : [...d, key] });
      persist();
    },
    toggleValue: (key, value) => {
      const cur = get().disabledValues[key] ?? [];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      // Toggling a value implies the axis is on.
      set({
        disabledAxes: get().disabledAxes.filter((x) => x !== key),
        disabledValues: { ...get().disabledValues, [key]: next },
      });
      persist();
    },
    setAxisValues: (key, on) => {
      set({
        disabledAxes: get().disabledAxes.filter((x) => x !== key),
        disabledValues: { ...get().disabledValues, [key]: on ? [] : [...valuesOf(key)] },
      });
      persist();
    },
    enableAllAxes: () => {
      set({ disabledAxes: [] });
      persist();
    },
    disableAllAxes: () => {
      set({ disabledAxes: getConceptAxes().map((a) => a.key) });
      persist();
    },
    resetAll: () => {
      set({ disabledAxes: [], disabledValues: {} });
      persist();
    },

    activeSelection: () => {
      const sel: FacetSelection = {};
      const { disabledAxes, disabledValues } = get();
      for (const axis of getConceptAxes()) {
        if (disabledAxes.includes(axis.key)) continue;
        const off = new Set(disabledValues[axis.key] ?? []);
        const values = axis.values.filter((v) => !off.has(v));
        if (values.length > 0) sel[axis.key] = values;
      }
      return sel;
    },
    activeAxisCount: () => Object.keys(get().activeSelection()).length,
  };
});
